import { useEffect } from 'react';
import { getAppStatus, onReconnect } from './appStatus';
import { clearOfflineCopy, offlineCopyUserId, syncOfflineCopy } from './offline';
import { syncPushSubscription } from './push';
import { queryClient } from './queryClient';

const OFFLINE_SYNC_MS = 10 * 60_000;

/**
 * While signed in: keep the offline copy fresh (at start, every 10 minutes, after
 * saving something, after reconnecting) and keep this device's push subscription linked.
 */
export function useBackgroundSync(userId: number): void {
  useEffect(() => {
    let cancelled = false;
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      if (!cancelled && getAppStatus().online) void syncOfflineCopy(userId);
    };

    void (async () => {
      const owner = offlineCopyUserId();
      if (owner !== null && owner !== userId) await clearOfflineCopy();
      sync();
    })();
    if (getAppStatus().online) void syncPushSubscription().catch(() => undefined);

    const interval = setInterval(sync, OFFLINE_SYNC_MS);
    const stopReconnect = onReconnect(sync);
    const stopMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'success') {
        clearTimeout(debounce);
        debounce = setTimeout(sync, 3000);
      }
    });
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(debounce);
      stopReconnect();
      stopMutations();
    };
  }, [userId]);
}
